import { ProductCategory } from 'enums/product.enum';
import { ReceptionItemKind } from 'enums/reception.enum';
import { ReceptionDTO } from 'models/reception';
import moment from 'moment';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import receptionService from 'services/reception.service';

interface EstimateItem {
    productName: string;
    quantity: number;
    unitPrice: number;
    unitCost: number;
    totalPrice: number;
    memo: string;
    isDiscount?: boolean;
    sourceItemIndex?: number;
    sourceItemId?: number;
    sourceType?: 'ITEM_GROUP' | 'STANDALONE' | 'DISCOUNT';
    parentName?: string;
    category?: ProductCategory;
    kind?: ReceptionItemKind;
    _optionIndex?: number;
}

interface EstimateModalProps {
    show: boolean;
    reception: ReceptionDTO;
    onClose: (estimateItems: EstimateItem[], updatedReception?: Partial<ReceptionDTO>) => void;
}

// 회사 기본 정보
const COMPANY_INFO = {
    companyName: '컴포텍',
    businessNumber: '447-87-01489',
    bankAccount: '신한은행 100-033-726931 (주)컴포텍',
    phone: '010-8273-6681',
    fax: '1533-2352',
    address: '광주광역시 광산구 비아로 147길 8-30',
    homepage: 'http://soonanbang.co.kr',
};

const LOGO_URL = `/assets/admin/img/common/logo.png`;
const SEAL_URL = `/assets/admin/img/common/seal.png`;

const EstimateModal: React.FC<EstimateModalProps> = ({ show, reception, onClose }) => {
    const printRef = useRef<HTMLDivElement>(null);
    const [estimateItems, setEstimateItems] = useState<EstimateItem[]>([]);
    const [emptyEstimateItems, setEmptyEstimateItems] = useState<EstimateItem[]>([]);

    // 수정 가능한 고객 정보 상태
    const [editableCustomerInfo, setEditableCustomerInfo] = useState({
        customerName: '',
        phone1: '',
        estimateDate: '',
        tripFee: 0
    });

    // 견적서 모달 ESC 핸들러
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && show) {
                onClose(estimateItems, {
                    customerName: editableCustomerInfo.customerName,
                    phone1: editableCustomerInfo.phone1,
                    estimateDate: editableCustomerInfo.estimateDate,
                    tripFee: editableCustomerInfo.tripFee
                });
            }
        };

        if (show) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [show, estimateItems, editableCustomerInfo, onClose]);

    // reception 데이터가 변경되면 견적서 아이템 업데이트
    useEffect(() => {
        if (reception?.items && reception.items.length > 0) {
            const items: EstimateItem[] = [];

            reception.items.forEach((item: any, itemIndex: number) => {
                if (item.options && item.options.length > 0) {
                    const hasFreeOption = item.options.some(
                        (opt: any) => opt.productName === '무상' && opt.quantity > 0
                    );

                    item.options.forEach((option: any, optionIndex: number) => {
                        if (option.productName === '무상') {
                            return;
                        }

                        const isBoiler = option.category === 'BOILER';
                        const parentName = item.modelName;

                        const qty = option.quantity || 1;
                        const unit = option.unitPrice || 0;
                        const unitCost = option.unitCost || 0;

                        const adjustedUnitPrice = hasFreeOption ? 0 : unit;
                        const adjustedTotalPrice = hasFreeOption ? 0 : (qty * unit);

                        items.push({
                            productName: option.productName,
                            quantity: qty,
                            unitPrice: adjustedUnitPrice,
                            unitCost: unitCost,
                            totalPrice: adjustedTotalPrice,
                            memo: isBoiler ? '' : (option.productName || ''),
                            parentName: isBoiler ? parentName : undefined,
                            sourceType: 'ITEM_GROUP',
                            sourceItemIndex: itemIndex,
                            sourceItemId: item.id,
                            category: undefined,
                            kind: item.kind
                        });
                    });
                }
            });

            setEstimateItems(items.length > 0 ? items : [
                { productName: '', quantity: 1, unitPrice: 0, unitCost: 0, totalPrice: 0, memo: '', sourceType: 'STANDALONE' },
            ]);

            let emptyItems: EstimateItem[] = [];
            for (let i = 0; i < 10 - items.length; i++) {
                emptyItems.push({
                    productName: '',
                    quantity: 1,
                    unitPrice: 0,
                    unitCost: 0,
                    totalPrice: 0,
                    memo: '',
                    sourceType: 'STANDALONE',
                    category: null,
                    kind: null
                });
            }
            setEmptyEstimateItems(emptyItems);
        }

        // 수정 가능한 고객 정보 초기화
        setEditableCustomerInfo({
            customerName: (reception as any)?.customerName || '',
            phone1: (reception as any)?.phone1 || '',
            estimateDate: moment().format('YYYY-MM-DD'),  // 항상 오늘 날짜
            tripFee: (reception as any)?.tripFee || 0
        });

    }, [reception]);

    // 고객 정보 수정 핸들러
    const handleCustomerInfoChange = useCallback((field: keyof typeof editableCustomerInfo, value: string | number) => {
        setEditableCustomerInfo(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    // 견적서 항목 추가
    const addEstimateItem = useCallback(() => {
        setEstimateItems((prev) => [
            ...prev,
            {
                productName: '',
                quantity: 1,
                unitPrice: 0,
                unitCost: 0,
                totalPrice: 0,
                memo: '',
                parentName: '',
                sourceType: 'STANDALONE',
                category: null,
                kind: null
            },
        ]);
        setEmptyEstimateItems((prev) => prev.slice(1));
    }, []);

    // 견적서 특정 항목 삭제
    const removeEstimateItem = useCallback((index: number) => {
        setEstimateItems((prev) => prev.filter((_, i) => i !== index));
        const emptyItem: EstimateItem = {
            productName: '',
            quantity: 1,
            unitPrice: 0,
            unitCost: 0,
            totalPrice: 0,
            memo: '',
            sourceType: 'STANDALONE',
            category: null,
            kind: null
        };

        setEmptyEstimateItems((prev) => [...prev, emptyItem]);
    }, []);

    // 견적서 항목 수정
    const updateEstimateItem = useCallback(
        <K extends keyof EstimateItem>(index: number, field: K, value: EstimateItem[K]) => {
            setEstimateItems((prev) => {
                const newItems = [...prev];
                const item = { ...newItems[index] };

                (item[field] as EstimateItem[K]) = value;

                if (field === 'quantity' || field === 'unitPrice') {
                    const q = field === 'quantity' ? (value as number) : item.quantity;
                    const u = field === 'unitPrice' ? (value as number) : item.unitPrice;
                    item.totalPrice = (q || 0) * (u || 0);
                }

                if ((field === "productName" || field === "parentName") && typeof value === 'string') {
                    if (value.includes('할인') && !item.isDiscount) {
                        item.isDiscount = true;
                        item.sourceType = 'DISCOUNT';
                    } else if (item.isDiscount && !value.includes('할인')) {
                        item.isDiscount = false;
                        item.sourceType = item.sourceType === 'DISCOUNT' ? 'STANDALONE' : item.sourceType;
                    }
                }

                newItems[index] = item;
                return newItems;
            });
        },
        []
    );

    // 총 금액 계산 (출장비 포함)
    const itemsTotal = estimateItems.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    const totalAmount = itemsTotal + (editableCustomerInfo.tripFee || 0);

    // 인쇄용 HTML을 생성 
    const buildPrintHTML = useCallback(() => {
        const logoUrl = LOGO_URL;
        const sealUrl = SEAL_URL;

        const today = moment().format('YYYY-MM-DD');

        if (!editableCustomerInfo.estimateDate) {
            setEditableCustomerInfo({
                ...editableCustomerInfo,
                estimateDate: today
            });
        }

        const estimateDate = editableCustomerInfo.estimateDate || today;
        const customerName = editableCustomerInfo.customerName;
        const phone1 = editableCustomerInfo.phone1;
        const tripFee = editableCustomerInfo.tripFee || 0;

        // 🔥 도/지역 정보 고정값 (reception에서 가져옴)
        const province = (reception as any)?.province || '';
        const city = (reception as any)?.city || '';
        const locationDisplay = [province, city].filter(Boolean).join(' ');

        const rows = estimateItems
            .map(
                (item) => `
        <tr>
          <td>${item.parentName || ''}</td>
          <td>${item.category === 'BOILER' ? '' : item.productName || ''}</td>
          <td>${item.quantity || ''}</td>
          <td class="text-right">${item.unitPrice ? item.unitPrice.toLocaleString('ko-KR') : ''}</td>
          <td class="text-right bold">${item.totalPrice ? item.totalPrice.toLocaleString('ko-KR') : '무상'}</td>
          <td>${item.memo || ''}</td>
        </tr>`
            )
            .join('');

        const emptyRows = emptyEstimateItems
            .map(
                (_, index) => `
        <tr>
          <td style="height: 38px;"><div style="min-height: 22px;">&nbsp;</div></td>
          <td style="height: 38px;"><div style="min-height: 22px;">&nbsp;</div></td>
          <td style="height: 38px;"><div style="min-height: 22px;">&nbsp;</div></td>
          <td style="height: 38px;"><div style="min-height: 22px;">&nbsp;</div></td>
          <td style="height: 38px;"><div style="min-height: 22px;">&nbsp;</div></td>
          <td style="height: 38px;"><div style="min-height: 22px;">&nbsp;</div></td>
        </tr>`
            )
            .join('');

        // 🔥 출장비 행 추가
        const tripFeeRow = tripFee > 0 ? `
        <tr style="background-color: #fffacd;">
          <td colspan="4" style="text-align: center; font-weight: bold;">출장비</td>
          <td class="text-right bold">${tripFee.toLocaleString('ko-KR')}</td>
          <td></td>
        </tr>` : '';

        const styles = `
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: serif; background: #fff; }
        table { border-collapse: collapse; width: 100%; }
        .bold { font-weight: bold; }

        .seal-inline {
            height: 44px;
            margin-left: 8px;
            vertical-align: middle;
            object-fit: contain;
        }
        @media print {
            .seal-inline { filter: none; }
        }

        .title-bar {
            display: grid;
            grid-template-columns: 120px 1fr 120px;
            align-items: center;
            margin-bottom: 30px;
            border-bottom: 3px solid #000;
            padding-bottom: 8px;
        }
        .title-bar .logo {
            height: 70px;
            object-fit: contain;
            justify-self: start;
        }
        .title-bar .title {
            font-size: 34px;
            font-weight: bold;
            letter-spacing: 8px;
            text-align: center;
            margin: 0;
            justify-self: center;
        }

        .info-table { width: 100%; font-size: 16px; margin-bottom: 15px; table-layout: fixed; }
        .info-table td { padding: 5px 0; white-space: nowrap; }
        .info-table .label { font-weight: bold; width: 110px; text-align: right; padding-right: 8px; }
        .info-table .customer-value { font-weight: 700; }

        .section-title {
        font-weight: bold; 
        background-color: #f0f0f0; 
        padding: 8px; 
        border: 2px solid #000;
        margin: 15px 0 0 0; 
        text-align: center;
        }

        .bank-account-highlight {
            background-color: #ffff00 !important;
            font-weight: bold;
            border: 2px solid #000; 
            padding: 12px;
            text-align: center;
            font-size: 18px;
            margin: 15px 0;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
        }

        .estimate-table {
        width: 100%; 
        border: 2px solid #000; 
        border-collapse: collapse; 
        font-size: 13px; 
        margin-top: 0;
        }
        .estimate-table th, .estimate-table td {
        border: 1px solid #000; 
        padding: 6px; 
        text-align: center;
        }
        .estimate-table th { 
        background-color: #f0f0f0; 
        font-weight: bold; 
        }

        .text-right { text-align: right; }

        .total-box {
        display: inline-block; 
        border: 2px solid #000; 
        padding: 8px 18px;
        background-color: #f0f0f0; 
        font-size: 15px; 
        font-weight: bold; 
        float: right; 
        margin: 15px 0;
        }

        .conditions { 
        margin: 25px 0 0 0; 
        font-size: 14px; 
        line-height: 1.7;
        page-break-inside: avoid;
        }
        .conditions h6 { 
        font-weight: bold; 
        font-size: 16px; 
        margin-bottom: 10px; 
        }

        #print-root {
        width: 210mm;
        min-height: 297mm;
        padding: 15mm;
        margin: 0 auto;
        background: #fff;
        }

        @page { 
        size: A4; 
        margin: 15mm; 
        }

        @media print {
        .title-bar { margin-bottom: 25px; padding-bottom: 6px; }
        .title-bar .logo { height: 65px; }
        .title-bar .title { font-size: 32px; }
        .info-table { margin-bottom: 12px; }
        .bank-account-highlight { padding: 10px; margin: 12px 0; font-size: 17px; }
        .section-title { padding: 7px; margin: 12px 0 0 0; }
        .estimate-table { font-size: 12px; }
        .estimate-table th, .estimate-table td { padding: 5px; }
        .total-box { padding: 7px 16px; margin: 12px 0; font-size: 14px; }
        .conditions { margin: 18px 0 0 0; font-size: 13px; line-height: 1.6; }
        .conditions h6 { font-size: 15px; margin-bottom: 8px; }
        }
      </style>
    `;

        const inner = `
<div id="print-root">
  <div class="title-bar">
      <img src="${logoUrl}" class="logo" alt="logo" crossorigin="anonymous" onerror="this.style.display='none'"/>
      <h1 class="title">견 적 서</h1>
      <div></div>
  </div>

  <table style="width: 100%; margin-bottom: 15px;">
    <tr>
      <td style="width: 50%; vertical-align: top;">
        <table style="width: 100%; table-layout: fixed;">
          <tr>
            <td style="width: 100px; font-weight: bold; font-size: 16px; padding: 5px 0;">견적일자</td>
            <td style="width: 10px; font-weight: bold; font-size: 16px; padding: 5px 0;">:</td>
            <td style="font-size: 16px; font-weight: 700; padding: 5px 0 5px 8px;">${estimateDate} ${locationDisplay}</td>
          </tr>
          <tr>
            <td style="width: 100px; font-weight: bold; font-size: 16px; padding: 5px 0;">고객명</td>
            <td style="width: 10px; font-weight: bold; font-size: 16px; padding: 5px 0;">:</td>
            <td style="font-size: 16px; font-weight: 700; padding: 5px 0 5px 8px;">${customerName}</td>
          </tr>
          <tr>
            <td style="width: 100px; font-weight: bold; font-size: 16px; padding: 5px 0;">전화번호</td>
            <td style="width: 10px; font-weight: bold; font-size: 16px; padding: 5px 0;">:</td>
            <td style="font-size: 16px; font-weight: 700; padding: 5px 0 5px 8px;">${phone1}</td>
          </tr>
        </table>
      </td>
      <td style="width: 50%; vertical-align: top;">
        <table style="width: 100%; table-layout: fixed;">
          <tr>
            <td style="width: 100px; font-weight: bold; padding: 3px 0;">사업자번호</td>
            <td style="width: 10px; font-weight: bold; padding: 3px 0;">:</td>
            <td style="padding: 3px 0 3px 8px;">${COMPANY_INFO.businessNumber}</td>
          </tr>
          <tr>
            <td style="width: 100px; font-weight: bold; padding: 3px 0; vertical-align: top;">상 호</td>
            <td style="width: 10px; font-weight: bold; padding: 3px 0; vertical-align: top;">:</td>
            <td style="padding: 3px 0 3px 8px; white-space: nowrap;">
              ${COMPANY_INFO.companyName}
              <img src="${sealUrl}" alt="seal" class="seal-inline" crossorigin="anonymous" onerror="this.style.display='none'"/>
            </td>
          </tr>
          <tr>
            <td style="width: 100px; font-weight: bold; padding: 3px 0;">전화번호</td>
            <td style="width: 10px; font-weight: bold; padding: 3px 0;">:</td>
            <td style="padding: 3px 0 3px 8px;">${COMPANY_INFO.phone}</td>
          </tr>
          <tr>
            <td style="width: 100px; font-weight: bold; padding: 3px 0;">주소</td>
            <td style="width: 10px; font-weight: bold; padding: 3px 0;">:</td>
            <td style="padding: 3px 0 3px 8px;">${COMPANY_INFO.address}</td>
          </tr>
          <tr>
            <td style="width: 100px; font-weight: bold; padding: 3px 0;">홈페이지</td>
            <td style="width: 10px; font-weight: bold; padding: 3px 0;">:</td>
            <td style="padding: 3px 0 3px 8px; color: blue;">${COMPANY_INFO.homepage}</td>
          </tr>
        </table>
      </td>
    </tr>
  </table>

  <div class="bank-account-highlight">입금계좌: ${COMPANY_INFO.bankAccount}</div>

  <div class="section-title">수리 및 판매내역</div>

  <table class="estimate-table">
    <thead>
      <tr>
        <th style="width: 20%">품목</th>
        <th style="width: 20%">부품/판매</th>
        <th style="width: 10%">수량</th>
        <th style="width: 15%">단가</th>
        <th style="width: 15%">금액</th>
        <th style="width: 15%">비고</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
      ${emptyRows}
      ${tripFeeRow}
    </tbody>
  </table>

  <div class="total-box">청구 금액: ${totalAmount.toLocaleString('ko-KR')}원</div>
  <div style="clear: both;"></div>

  <div class="conditions">
      <h6>유의 사항</h6>
      <div>1. 견적금액은 부가세포함가입니다.</div>
      <div>2. 세금계산서 발행원하시면 사업자등록증보내주세요.</div>
      <div>3. 현금영수증 필요하시면 핸드폰 번호 알려 주세요</div>
      <div>4. A/S는 소비자 과실이 없는 경우 1년 무상입니다.</div>
  </div>

</div>
`;

        return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>견적서</title>
          ${styles}
        </head>
        <body>
          ${inner}
        </body>
      </html>
    `;
    }, [reception, estimateItems, totalAmount, editableCustomerInfo, emptyEstimateItems]);

    // 인쇄 핸들러
    const handlePrint = useCallback(async () => {
        const w = window.open('', '_blank');
        if (!w) return;
        if (reception.id) await receptionService.updateEstimateIssueDate(reception.id);
        w.document.write(buildPrintHTML());
        w.document.close();
        setTimeout(() => {
            w.focus();
            w.print();
            w.close();
        }, 300);
    }, [buildPrintHTML, reception.id]);

    // 이미지 저장 핸들러
    const handleSaveAsImage = useCallback(async () => {
        if (reception.id) await receptionService.updateEstimateIssueDate(reception.id);

        const filename = `견적서_${editableCustomerInfo.customerName || '고객'}_${new Date()
            .toISOString()
            .slice(0, 10)}.png`;

        try {
            if ('showSaveFilePicker' in window) {
                const fileHandle = await (window as any).showSaveFilePicker({
                    suggestedName: filename,
                    types: [{
                        description: 'PNG 이미지',
                        accept: { 'image/png': ['.png'] }
                    }]
                });

                const htmlForCapture = `
                ${buildPrintHTML()}
                <script src="https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js"></script>
                <script>
                    window.addEventListener('load', async () => {
                        try {
                            const el = document.getElementById('print-root');
                            await new Promise(r => setTimeout(r, 50));
                            const canvas = await html2canvas(el, {
                                scale: 2,
                                backgroundColor: '#ffffff',
                                useCORS: true,
                                logging: false
                            });
                            
                            canvas.toBlob(async (blob) => {
                                try {
                                    window.opener.postMessage({
                                        type: 'SAVE_IMAGE',
                                        blob: blob
                                    }, '*');
                                    window.close();
                                } catch (e) {
                                    console.error('파일 저장 오류', e);
                                    alert('파일 저장 중 오류가 발생했습니다.');
                                }
                            }, 'image/png');
                        } catch (e) {
                            console.error('이미지 생성 오류', e);
                            alert('이미지 생성 중 오류가 발생했습니다.');
                        }
                    });
                </script>
            `;

                const messageHandler = async (event: MessageEvent) => {
                    if (event.data.type === 'SAVE_IMAGE') {
                        try {
                            const writable = await fileHandle.createWritable();
                            await writable.write(event.data.blob);
                            await writable.close();
                            alert('이미지가 성공적으로 저장되었습니다.');
                        } catch (e) {
                            console.error('파일 쓰기 오류', e);
                            alert('파일 저장 중 오류가 발생했습니다.');
                        }
                        window.removeEventListener('message', messageHandler);
                    }
                };
                window.addEventListener('message', messageHandler);

                const w = window.open('', '_blank');
                if (!w) return;
                w.document.write(htmlForCapture);
                w.document.close();

            } else {
                const htmlForCapture = `
                ${buildPrintHTML()}
                <script src="https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js"></script>
                <script>
                    window.addEventListener('load', async () => {
                        try {
                            const el = document.getElementById('print-root');
                            await new Promise(r => setTimeout(r, 50));
                            const canvas = await html2canvas(el, {
                                scale: 2,
                                backgroundColor: '#ffffff',
                                useCORS: true,
                                logging: false
                            });
                            const a = document.createElement('a');
                            a.download = ${JSON.stringify(filename)};
                            a.href = canvas.toDataURL('image/png');
                            document.body.appendChild(a);
                            a.click();
                            setTimeout(() => window.close(), 300);
                        } catch (e) {
                            console.error('이미지 저장 오류', e);
                            alert('이미지 저장 중 오류가 발생했습니다.');
                        }
                    });
                </script>
            `;

                const w = window.open('', '_blank');
                if (!w) return;
                w.document.write(htmlForCapture);
                w.document.close();
            }
        } catch (e: any) {
            if (e.name === 'AbortError') {
                console.log('사용자가 저장을 취소했습니다.');
            } else {
                console.error('파일 선택 오류', e);
                alert('파일 선택 중 오류가 발생했습니다.');
            }
        }
    }, [buildPrintHTML, editableCustomerInfo.customerName, reception.id]);

    // 🔥 도/지역 정보 계산 (화면 표시용)
    const province = (reception as any)?.province || '';
    const city = (reception as any)?.city || '';
    const locationDisplay = [province, city].filter(Boolean).join(' ');

    if (!show) return null;

    return (
        <>
            <div
                className="modal fade show"
                style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1070 }}
                tabIndex={-1}
                role="dialog"
            >
                <div className="modal-dialog modal-xl" role="document" style={{ margin: '10px auto' }}>
                    <div className="modal-content">
                        <div className="modal-header bg-info text-white">
                            <h4 className="modal-title">
                                <i className="fas fa-file-invoice mr-2" />
                                견적서 발행
                            </h4>
                            <div className="d-flex">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light mr-2"
                                    onClick={handleSaveAsImage}
                                    title="이미지로 저장"
                                >
                                    <i className="fas fa-image mr-1" />
                                    저장
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-success mr-2"
                                    onClick={handlePrint}
                                    title="인쇄"
                                >
                                    <i className="fas fa-print mr-1" />
                                    인쇄
                                </button>
                                <button type="button" className="close text-white" onClick={() => onClose(estimateItems, {
                                    customerName: editableCustomerInfo.customerName,
                                    phone1: editableCustomerInfo.phone1,
                                    estimateDate: editableCustomerInfo.estimateDate,
                                    tripFee: editableCustomerInfo.tripFee
                                })}>
                                    <span>&times;</span>
                                </button>
                            </div>
                        </div>

                        <div className="modal-body p-0" style={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }}>
                            <div ref={printRef} className="estimate-document bg-white" style={{ padding: '40px', fontFamily: 'serif' }}>
                                {/* 견적서 제목 바 */}
                                <div
                                    className="title-bar"
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '120px 1fr 120px',
                                        alignItems: 'center',
                                        marginBottom: '40px',
                                        borderBottom: '3px solid #000',
                                        paddingBottom: '10px',
                                    }}
                                >
                                    <img
                                        src={LOGO_URL}
                                        alt="logo"
                                        crossOrigin="anonymous"
                                        onError={(e) => ((e.currentTarget.style.display = 'none'))}
                                        style={{
                                            height: '80px',
                                            objectFit: 'contain',
                                            justifySelf: 'start',
                                        }}
                                    />
                                    <h1
                                        style={{
                                            fontSize: '36px',
                                            fontWeight: 'bold',
                                            letterSpacing: '8px',
                                            textAlign: 'center',
                                            justifySelf: 'center',
                                            margin: 0,
                                        }}
                                    >
                                        견 적 서
                                    </h1>
                                    <div />
                                </div>

                                {/* 상단 정보 영역 */}
                                <div className="row mb-4">
                                    <div className="col-6">
                                        <table style={{ width: '100%', fontSize: '14px' }}>
                                            <tbody>
                                                <tr>
                                                    <td style={{ fontWeight: 'bold', width: '100px', padding: '5px 0', fontSize: '16px', whiteSpace: 'nowrap' }}>견적일자 :</td>
                                                    <td style={{ padding: '5px 0', whiteSpace: 'nowrap' }}>
                                                        <input
                                                            type="date"
                                                            value={editableCustomerInfo.estimateDate || ''}
                                                            onChange={(e) => handleCustomerInfoChange('estimateDate', e.target.value)}
                                                            style={{
                                                                border: '1px solid #ccc',
                                                                padding: '4px 8px',
                                                                fontSize: '16px',
                                                                fontWeight: '700',
                                                                borderRadius: '3px',
                                                            }}
                                                        />
                                                        {locationDisplay && (
                                                            <span style={{ marginLeft: '8px', fontSize: '16px', fontWeight: '600' }}>
                                                                {locationDisplay}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ fontWeight: 'bold', width: '100px', padding: '5px 0', fontSize: '16px', whiteSpace: 'nowrap' }}>고객명 :</td>
                                                    <td style={{ padding: '5px 0', whiteSpace: 'nowrap' }}>
                                                        <input
                                                            type="text"
                                                            value={editableCustomerInfo.customerName}
                                                            onChange={(e) => handleCustomerInfoChange('customerName', e.target.value)}
                                                            placeholder="고객명을 입력하세요"
                                                            style={{
                                                                border: '1px solid #ccc',
                                                                padding: '4px 8px',
                                                                fontSize: '16px',
                                                                fontWeight: '700',
                                                                borderRadius: '3px',
                                                                width: '100%'
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ fontWeight: 'bold', width: '100px', padding: '5px 0', fontSize: '16px', whiteSpace: 'nowrap' }}>전화번호 :</td>
                                                    <td style={{ padding: '5px 0', whiteSpace: 'nowrap' }}>
                                                        <input
                                                            type="text"
                                                            value={editableCustomerInfo.phone1}
                                                            onChange={(e) => handleCustomerInfoChange('phone1', e.target.value)}
                                                            placeholder="전화번호를 입력하세요"
                                                            style={{
                                                                border: '1px solid #ccc',
                                                                padding: '4px 8px',
                                                                fontSize: '16px',
                                                                fontWeight: '700',
                                                                borderRadius: '3px',
                                                                width: '100%'
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="col-6">
                                        <table style={{ width: '100%', fontSize: '14px' }}>
                                            <tbody>
                                                <tr>
                                                    <td style={{ fontWeight: 'bold', width: '110px', padding: '3px 0' }}>사업자번호 :</td>
                                                    <td style={{ padding: '3px 0', whiteSpace: 'nowrap' }}>{COMPANY_INFO.businessNumber}</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ fontWeight: 'bold', width: '110px', padding: '3px 0', verticalAlign: 'top' }}>상 호 :</td>
                                                    <td style={{
                                                        padding: '3px 0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        minHeight: '50px',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        <span>{COMPANY_INFO.companyName}</span>
                                                        <img src={SEAL_URL} alt="seal"
                                                            crossOrigin="anonymous"
                                                            onError={(e) => (e.currentTarget.style.display = 'none')}
                                                            style={{
                                                                height: '44px',
                                                                marginLeft: '8px',
                                                                objectFit: 'contain',
                                                                flexShrink: 0
                                                            }} />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ fontWeight: 'bold', width: '110px', padding: '3px 0' }}>전화번호 :</td>
                                                    <td style={{ padding: '3px 0', whiteSpace: 'nowrap' }}>{COMPANY_INFO.phone}</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ fontWeight: 'bold', width: '110px', padding: '3px 0' }}>주소 :</td>
                                                    <td style={{ padding: '3px 0', whiteSpace: 'nowrap' }}>{COMPANY_INFO.address}</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ fontWeight: 'bold', width: '110px', padding: '3px 0' }}>홈페이지 :</td>
                                                    <td style={{ padding: '3px 0', color: 'blue', whiteSpace: 'nowrap' }}>{COMPANY_INFO.homepage}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* 입금계좌 강조 영역 */}
                                <div className="text-center mb-3">
                                    <div
                                        style={{
                                            backgroundColor: '#ffff00',
                                            border: '2px solid #000',
                                            padding: '15px',
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            margin: '20px 0',
                                        }}
                                    >
                                        입금계좌: {COMPANY_INFO.bankAccount}
                                    </div>
                                </div>

                                {/* 수리 및 판매내역 제목 */}
                                <div className="text-center mb-3">
                                    <h5
                                        style={{
                                            fontWeight: 'bold',
                                            backgroundColor: '#f0f0f0',
                                            padding: '10px',
                                            border: '2px solid #000',
                                            margin: '0',
                                        }}
                                    >
                                        수리 및 판매내역
                                    </h5>
                                </div>

                                {/* 견적 항목 테이블 */}
                                <div className="mb-4">
                                    <table
                                        style={{
                                            width: '100%',
                                            border: '2px solid #000',
                                            borderCollapse: 'collapse',
                                            fontSize: '14px',
                                        }}
                                    >
                                        <thead>
                                            <tr style={{ backgroundColor: '#f0f0f0' }}>
                                                <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', fontWeight: 'bold', width: '20%' }}>품목</th>
                                                <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', fontWeight: 'bold', width: '20%' }}>부품/판매</th>
                                                <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', fontWeight: 'bold', width: '10%' }}>수량</th>
                                                <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', fontWeight: 'bold', width: '15%' }}>단가</th>
                                                <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', fontWeight: 'bold', width: '15%' }}>금액</th>
                                                <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', fontWeight: 'bold', width: '15%' }}>비고</th>
                                                <th className="no-print" style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', fontWeight: 'bold', width: '5%' }}>삭제</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {estimateItems.map((item, index) => (
                                                <tr key={index}>
                                                    <td style={{ border: '1px solid #000', padding: '8px' }}>
                                                        {(!item._optionIndex || item._optionIndex == 0) &&
                                                            <input
                                                                type="text"
                                                                value={item.kind === 'DISCOUNT' ? item.productName || item.parentName : item.parentName}
                                                                onChange={(e) => {
                                                                    updateEstimateItem(index, item.kind === 'DISCOUNT' ? 'productName' : 'parentName', e.target.value);
                                                                }}
                                                                style={{
                                                                    width: '100%',
                                                                    border: 'none',
                                                                    outline: 'none',
                                                                    backgroundColor: 'transparent',
                                                                    fontSize: '14px',
                                                                }}
                                                            />
                                                        }
                                                    </td>
                                                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                                                        <input
                                                            type="text"
                                                            disabled={item.category === 'BOILER' || item.kind === 'DISCOUNT'}
                                                            value={item.category === 'BOILER' || item.kind === 'DISCOUNT' ? '' : item.productName}
                                                            onChange={(e) => updateEstimateItem(index, 'productName', e.target.value)}
                                                            style={{
                                                                width: '100%',
                                                                border: 'none',
                                                                outline: 'none',
                                                                backgroundColor: 'transparent',
                                                                fontSize: '14px',
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                                                        <input
                                                            type="number"
                                                            value={item.quantity || ''}
                                                            onChange={(e) => updateEstimateItem(index, 'quantity', Number(e.target.value) || 0)}
                                                            style={{
                                                                width: '100%',
                                                                border: 'none',
                                                                outline: 'none',
                                                                backgroundColor: 'transparent',
                                                                textAlign: 'center',
                                                                fontSize: '14px',
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'right' }}>
                                                        <input
                                                            type="text"
                                                            value={
                                                                item.unitPrice === 0
                                                                    ? ''
                                                                    : item.isDiscount
                                                                        ? Math.abs(item.unitPrice).toString()
                                                                        : item.unitPrice.toString()
                                                            }
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                if (value === '' || /^\d*$/.test(value)) {
                                                                    let numValue = value === '' ? 0 : Number(value);
                                                                    if (item.isDiscount && numValue > 0) {
                                                                        numValue = -numValue;
                                                                    }
                                                                    updateEstimateItem(index, 'unitPrice', numValue);
                                                                }
                                                            }}
                                                            placeholder={item.isDiscount ? "할인 금액 입력" : "금액 입력"}
                                                            style={{
                                                                width: '100%',
                                                                border: 'none',
                                                                outline: 'none',
                                                                backgroundColor: 'transparent',
                                                                textAlign: 'right',
                                                                fontSize: '14px',
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                                                        {item.totalPrice ? item.totalPrice.toLocaleString('ko-KR') : '무상'}
                                                    </td>
                                                    <td style={{ border: '1px solid #000', padding: '8px' }}>
                                                        <input
                                                            type="text"
                                                            value={item.memo}
                                                            onChange={(e) => updateEstimateItem(index, 'memo', e.target.value)}
                                                            style={{
                                                                width: '100%',
                                                                border: 'none',
                                                                outline: 'none',
                                                                backgroundColor: 'transparent',
                                                                fontSize: '14px',
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="no-print" style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => removeEstimateItem(index)}
                                                            disabled={estimateItems.length <= 1}
                                                            style={{ fontSize: '12px', padding: '2px 6px' }}
                                                        >
                                                            <i className="fas fa-trash" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}

                                            {emptyEstimateItems.map((_, index) => (
                                                <tr key={`empty-${index}`}>
                                                    <td style={{ border: '1px solid #000', padding: '8px', height: '38px' }}><div style={{ minHeight: '22px' }}>&nbsp;</div></td>
                                                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', height: '38px' }}><div style={{ minHeight: '22px' }}>&nbsp;</div></td>
                                                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', height: '38px' }}><div style={{ minHeight: '22px' }}>&nbsp;</div></td>
                                                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'right', height: '38px' }}><div style={{ minHeight: '22px' }}>&nbsp;</div></td>
                                                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'right', fontWeight: 'bold', height: '38px' }}><div style={{ minHeight: '22px' }}>&nbsp;</div></td>
                                                    <td style={{ border: '1px solid #000', padding: '8px', height: '38px' }}><div style={{ minHeight: '22px' }}>&nbsp;</div></td>
                                                    <td className="no-print" style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', height: '38px' }}><div style={{ minHeight: '22px' }}>&nbsp;</div></td>
                                                </tr>
                                            ))}

                                            {/* 🔥 출장비 행 추가 */}
                                            {editableCustomerInfo.tripFee > 0 && (
                                                <tr style={{ backgroundColor: '#fffacd' }}>
                                                    <td colSpan={4} style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>
                                                        출장비
                                                    </td>
                                                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                                                        <input
                                                            type="number"
                                                            value={editableCustomerInfo.tripFee || ''}
                                                            onChange={(e) => handleCustomerInfoChange('tripFee', Number(e.target.value) || 0)}
                                                            style={{
                                                                width: '100%',
                                                                border: 'none',
                                                                outline: 'none',
                                                                backgroundColor: 'transparent',
                                                                textAlign: 'right',
                                                                fontSize: '14px',
                                                                fontWeight: 'bold'
                                                            }}
                                                        />
                                                    </td>
                                                    <td colSpan={2} style={{ border: '1px solid #000', padding: '8px' }}></td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* 편집 버튼 영역 */}
                                <div className="mb-3 no-print">
                                    <button type="button" className="btn btn-sm btn-default mr-2" onClick={addEstimateItem}>
                                        <i className="fas fa-plus mr-1" />
                                        행 추가
                                    </button>
                                </div>

                                {/* 합계 영역 */}
                                <div className="text-right mb-4">
                                    <div
                                        style={{
                                            display: 'inline-block',
                                            border: '2px solid #000',
                                            padding: '10px 20px',
                                            backgroundColor: '#f0f0f0',
                                        }}
                                    >
                                        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                            청구 금액: {totalAmount.toLocaleString('ko-KR')}원
                                        </span>
                                    </div>
                                </div>

                                {/* 거래 조건 */}
                                <div className="mb-4">
                                    <div style={{ fontSize: '13px', lineHeight: '1.8' }}>
                                        <h6>유의 사항</h6>
                                        <div>1. 견적금액은 부가세포함가입니다.</div>
                                        <div>2. 세금계산서 발행원하시면 사업자등록증보내주세요.</div>
                                        <div>3. 현금영수증 필요하시면 핸드폰 번호 알려 주세요</div>
                                        <div>4. A/S는 소비자 과실이 없는 경우 1년 무상입니다.</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 푸터 */}
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={() => onClose(estimateItems, {
                                customerName: editableCustomerInfo.customerName,
                                phone1: editableCustomerInfo.phone1,
                                estimateDate: editableCustomerInfo.estimateDate,
                                tripFee: editableCustomerInfo.tripFee
                            })}>
                                <i className="fas fa-times mr-1" />
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 인쇄용 스타일 */}
            <style
                dangerouslySetInnerHTML={{
                    __html: `
                @media print {
                    * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }
                    body { margin: 0 !important; padding: 0 !important; background: white !important; }
                    .modal, .modal-dialog, .modal-content {
                    position: static !important; display: block !important; width: auto !important; height: auto !important;
                    margin: 0 !important; padding: 0 !important; border: none !important; box-shadow: none !important; background: white !important;
                    }
                    .modal-header, .modal-footer, .no-print, .btn { display: none !important; }
                    .modal-body { padding: 0 !important; margin: 0 !important; }
                    .estimate-document { padding: 20px !important; margin: 0 !important; background: white !important; width: 100% !important; min-height: 100vh !important; }
                    input {
                    border: none !important; background: transparent !important; box-shadow: none !important;
                    outline: none !important; -webkit-appearance: none !important; appearance: none !important;
                    }
                    table { border-collapse: collapse !important; }
                    @page { size: A4; margin: 15mm; }
                }
                .estimate-document input:focus { outline: 1px solid #007bff !important; }
                `,
                }}
            />
        </>
    );
};

export default EstimateModal;